import { type ProductListParams, type ProductSourcesParams } from './schemas';

// Query keys centralizadas para invalidaciones consistentes.
// Patrón: all -> lists/details/etc -> list(params)/detail(sku) (TanStack Query best practice).
export const productsKeys = {
  all: ['products'] as const,
  lists: () => [...productsKeys.all, 'list'] as const,
  // El área activa entra en la key: dos áreas resuelven proyecciones distintas
  // del mismo query, así que no pueden compartir cache. `null` para "sin área".
  list: (params: ProductListParams, areaSlug?: string | null) =>
    [...productsKeys.lists(), params, areaSlug ?? null] as const,
  details: () => [...productsKeys.all, 'detail'] as const,
  // sourceSlug + includePendings + area entran en la key para cachear
  // separado. Cambiar de área invalida cache automáticamente.
  detail: (sku: string, sourceSlug?: string, includePendings?: boolean, areaSlug?: string | null) =>
    [
      ...productsKeys.details(),
      sku,
      sourceSlug ?? null,
      includePendings ? 'with-pendings' : 'basic',
      areaSlug ?? null,
    ] as const,
  // Prefix por-sku para invalidar TODAS las variantes del detail de un
  // producto (todas las combinaciones de sourceSlug/includePendings/areaSlug).
  // `invalidateQueries({ queryKey: detail(sku) })` NO matchea el cache real
  // porque completa con defaults (`null/basic/null`) que suelen diferir de
  // la key en uso (usualmente `with-pendings` + area). Usar este helper para
  // invalidaciones tras write mutations.
  detailBySku: (sku: string) => [...productsKeys.details(), sku] as const,
  // Apagados por regla de activación de un SKU. No depende del área ni de la
  // fuente: la regla apaga la fila master del producto.
  activationBlocks: (sku: string) => [...productsKeys.all, 'activation-blocks', sku] as const,
  // Catálogo de áreas para el selector global.
  areas: () => [...productsKeys.all, 'areas'] as const,
  // Valores de un atributo para su filtro. El área entra en la key: cada área
  // lee fuentes distintas y resuelve valores distintos.
  attributeValues: (pointer: string, areaSlug?: string | null) =>
    [...productsKeys.all, 'attribute-values', pointer, areaSlug ?? null] as const,
  // Schema dinámico de campos editables: una sola entrada global.
  productSchema: () => [...productsKeys.all, 'schema'] as const,
  // Contratos tipados de extendedProperties (45 keys conocidas).
  // Endpoint separado para mantener el jsonSchema "limpio".
  productExtendedContracts: () => [...productsKeys.all, 'schema', 'extended-contracts'] as const,
  // AI configs resueltos por producto (jerarquía cat → ancestros → global).
  // productId en la key — refetch automático al cambiar de producto.
  resolvedAiConfigs: (productId: string) =>
    [...productsKeys.all, 'resolved-ai-configs', productId] as const,
  // Catálogo liviano de fuentes activas (GET /admin/products/sources).
  // params en la key para cachear por filtro (canRead/canWrite/isActive).
  sources: (params: ProductSourcesParams = {}) => [...productsKeys.all, 'sources', params] as const,
  // Versiones por productId + fuente + página. sourceSlug/page/limit en la
  // key para cachear cada combinación por separado (una fuente puede tener
  // cientos de versiones repartidas en muchas páginas, y el selector de
  // fuentes pide además un `limit=1` liviano solo para el conteo). Por SKU:
  // el endpoint de versiones busca `where: { sku }`.
  versionCounts: (sku: string) => [...productsKeys.all, 'version-counts', sku] as const,
  // PREFIJO de todas las versiones de un SKU, sin fuente ni página. Es lo que
  // hay que usar para invalidar tras guardar: `versions(sku)` completa la key
  // con `null` en fuente/página/limit, y TanStack compara elemento por
  // elemento — `null` contra `'sap'` no matchea, así que la página cacheada
  // de esa fuente nunca se refrescaba y la versión recién escrita no aparecía
  // hasta recargar. Mismo motivo por el que existe `detailBySku`.
  versionsBySku: (sku: string) => [...productsKeys.all, 'versions', sku] as const,
  versions: (sku: string, sourceSlug?: string, page?: number, limit?: number) =>
    [
      ...productsKeys.all,
      'versions',
      sku,
      sourceSlug ?? null,
      page ?? null,
      limit ?? null,
    ] as const,
};
